import React, { useCallback, useContext, useEffect, useState, useReducer } from "react";
import axios from "axios";
import { Dialog, DialogTitle, DialogContent, CircularProgress } from "@mui/material";
import DoneIcon from "@mui/icons-material/Done";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import DangerousIcon from "@mui/icons-material/Dangerous";
import "react-toastify/dist/ReactToastify.css";

import DownloadUploadFields from "../../components/PullListComponents/DownloadUploadFields";
import FileLoadStatus from "../../components/PullListComponents/FileLoadStatus";
import Status from "../../components/PullListComponents/Status";
import ShipmentFromRepo from "../../components/PullListComponents/ShipmentFromRepo";
import RushOrder from "../../components/PullListComponents/RushOrder";
import DisposalReason from "../../components/PullListComponents/DisposalReason";
import PullListComment from "../../components/PullListComponents/PullListComment";
import NumberOfSamples from "../../components/PullListComponents/NumberOfSamples";
import ShippingTemperature from "../../components/PullListComponents/ShippingTemperature";
import DispositionOfSample from "../../components/PullListComponents/DispositionOfSample";
import LabDetails from "../../components/PullListComponents/LabDetails";
import LegacyLabsDetails from "../../components/PullListComponents/LegacyLabDetails";
import InformedConsent from "../../components/PullListComponents/InformedConsent";
import InformedConsentApprover from "../../components/PullListComponents/InformedConsentApprover";
import InformedConsentComment from "../../components/PullListComponents/InformedConsentComment";
import DateSelector from "../../components/PullListComponents/DateSelector";
import Courier from "../../components/PullListComponents/Courier";
import TrackingNumber from "../../components/PullListComponents/TrackingNumber";
import ValidationStatus from "../../components/PullListComponents/ValidationStatus";
import ValidationBy from "../../components/PullListComponents/ValidationBy";
import RelatedJiraTickets from "../../components/PullListComponents/RelatedJiraTickets";
import DatasetsValidationLink from "../../components/PullListComponents/DatasetsValidationLink";
import ValidationNotesText from "../../components/PullListComponents/ValidationNotesText";
import EPUpdate from "../../components/PullListComponents/EPUpdate";
import BoxFileManager from "../../components/XplanComponents/BoxFileManager";
import HistoryLogTotal from "../../components/PullListComponents/HistoryLogTotal";
import UnloadPrompt from "../../components/Util/UnloadPrompt";
import { getRequiredWFFields } from "../../components/Util/RequiredFields";
import GroupBiorepositoryOps from "../../components/FieldTags/GroupBDRBiologists";

import { checkRole, formatValue, getFileVersions, handleFileUpload, hasNonEmptyFiles, isDataChanged } from "../../util/FormUtils";
import ROLES from "../../util/roles";
import { UserContext } from "../../context/UserContext";
import BASE_URL from "../../config/apiConfig";
import { showToast, showErrorToast } from "../../util/showToast";
import ServiceRequestNumber from '../../components/PullListComponents/ServiceRequestNumber';

const formReducer = (state, action) => {
    switch (action.type) {
        case "UPDATE_FORM":
            return action.updatedForm;
        default:
            return state;
    }
};

const PullListFormEdit = ({ isOpen, onClose, setHasUnsavedChanges, index, form, updateForm, pullListFolderId, sampleDisposal, lab, labLegacyAddress, couriers, isEPApproved }) => {
    const { user } = useContext(UserContext);
    const defaultRequired = ["informedConsent", "informedConsentApprover"];

    const [shouldClose, setShouldClose] = useState(false);
    const [editForm, dispatchEditForm] = useReducer(formReducer, { ...form });
    const [required, setRequired] = useState(defaultRequired);
    const [isSaving, setIsSaving] = useState(false);
    const [isSavingClosing, setIsSavingClosing] = useState(false);
    const [isApproving, setIsApproving] = useState(false);
    const [isShipping, setIsShipping] = useState(false);

    useEffect(() => {
        if (isOpen === true) {
            dispatchEditForm({ type: "UPDATE_FORM", updatedForm: form });
        }
    }, [isOpen]);

    useEffect(() => {
        if (shouldClose) {
            setShouldClose(false);
            onClose();
        }
    }, [shouldClose]);

    useEffect(() => {
        const updateRequiredFields = () => {
            const requiredFields = new Set(defaultRequired);

            ["informedConsent", "informedConsentApprover"].forEach(field => {
                const fieldsRequired = getRequiredWFFields(field, editForm.data);
                fieldsRequired.forEach(subField => {
                    requiredFields.add(subField);
                });
            });

            setRequired(Array.from(requiredFields));
        };
        updateRequiredFields();
    }, [editForm.data.informedConsent, editForm.data.informedConsentApprover, editForm.data.disposalReason]);

    const handleChange = useCallback(
        (name, value) => {
            const newValue = formatValue(value);
            const newData = { ...editForm.data, [name]: newValue };
            dispatchEditForm({
                type: "UPDATE_FORM",
                updatedForm: { ...editForm, data: newData },
            });
        },
        [editForm, dispatchEditForm]
    );

    const handleConsentChange = useCallback(
        (name, value) => {
            const newValue = formatValue(value);
            let newData;

            if (newValue === "Non-ITN") {
                newData = { ...editForm.data, informedConsent: newValue, informedConsentApprover: newValue }
            } else if (name === "informedConsent" && newValue === "No") {
                newData = { ...editForm.data, [name]: newValue, status: "Pull List Development" };
            } else {
                newData = { ...editForm.data, [name]: newValue };
            }

            dispatchEditForm({
                type: "UPDATE_FORM",
                updatedForm: { ...editForm, data: newData },
            });
        },
        [editForm, dispatchEditForm]
    );

    const handleWorkflowChange = useCallback(
        (name, value) => {
            const newValue = formatValue(value);
            let newData;

            if ((newValue === "No" && sampleDisposal === "No") && (
                editForm.data.status === "Pull List Development" ||
                editForm.data.status === "Pending Pull List Approval" ||
                editForm.data.status === "Pull List Approved" ||
                editForm.data.status === "Submitted to Repository" ||
                editForm.data.status === "Pull List Executed")) {
                newData = { ...editForm.data, [name]: newValue, status: "Real Time" }
            } else {
                newData = { ...editForm.data, [name]: newValue, status: "Pull List Development" };
            }

            dispatchEditForm({
                type: "UPDATE_FORM",
                updatedForm: { ...editForm, data: newData },
            });
        },
        [editForm, dispatchEditForm]
    );

    const handleFileChange = (category, file, form) => {
        const newForm = { ...form };
        switch (category) {
            case "pullListFile":
                newForm.pullListFile = file;
                editForm.data.fileLoadStatus = null;
                if (["Pull List Development", "Pending Pull List Approval", "Pull List Approved"].includes(editForm.data.status)) {
                    editForm.data.status = "Pull List Development";
                    editForm.data.informedConsent = "No";
                    editForm.data.informedConsentApprover = "No";
                    editForm.data.approvalBy = null;
                    editForm.data.approvalDate = null;
                }
                break;
            case "sampleQualityFile":
                newForm.sampleQualityFile = file;
                break;
            case "disposalRequestFile":
                newForm.disposalRequestFile = file;
                break;
            default:
                break;
        }
        dispatchEditForm({ type: "UPDATE_FORM", updatedForm: newForm });
    };

    const handleUploadAll = async (newData) => {
        let newFileVersion = 1;

        if (newData.pullListFile && editForm.pullListFile) {
            newFileVersion = await getFileVersions(newData.pullListFile) + 1;
        }

        const pullListResponse = await handleFileUpload(
            newData.boxId,
            `IC Verification and Pull List EP${form.data.epId}.${newData.pullListId}.${newFileVersion}.`,
            newData.pullListFile,
            editForm.pullListFile
        );
        if (pullListResponse) {
            newData.pullListFile = pullListResponse.data.id;
            newData.pullListFileVersion = newFileVersion;
        }

        const sampleQualityResponse = await handleFileUpload(
            newData.boxId,
            "EP" + form.data.epId + "_" + "PL" + newData.pullListId + "_SampleQuality.",
            newData.sampleQualityFile,
            editForm.sampleQualityFile
        );
        if (sampleQualityResponse) {
            newData.sampleQualityFile = sampleQualityResponse.data.id;
            newData.sampleQualityFileVersion = await getFileVersions(newData.sampleQualityFile);
        }

        const disposalRequestResponse = await handleFileUpload(
            newData.boxFilesId,
            "EP" + form.data.epId + "_" + "PL" + newData.pullListId + "_DisposalRequestDocumentation.",
            newData.disposalRequestFile,
            editForm.disposalRequestFile
        );
        if (disposalRequestResponse) {
            newData.disposalRequestFile = disposalRequestResponse.data.id;
            newData.disposalRequestFileVersion = await getFileVersions(newData.disposalRequestFile);
        }
    };

    const loadData = async (newData, shouldCloseSubmit) => {
        if (isDataChanged(newData, form.data) || hasNonEmptyFiles([editForm.pullListFile, editForm.sampleQualityFile, editForm.disposalRequestFile])) {
            if (!editForm.isNewSeries) {
                if ((newData.status === "Pull List Approved" || newData.status === "Submitted to Repository") &&
                    newData.shipDate &&
                    newData.serviceRequestNumber &&
                    ((newData.shippingTemperature && newData.dispositionOfSample && newData.courier && newData.trackingNumber) || sampleDisposal === "Yes")
                ) {
                    newData.status = "Pull List Executed";
                }

                const response = await axios.get(BASE_URL + `/api/pullLists/ep/${newData.epId}/pl/${newData.pullListId}/latest`, { withCredentials: true });
                const versionCheck = response.data.version;

                if (versionCheck >= newData.version) {
                    newData.version = 1 + versionCheck;
                }
            }

            await handleUploadAll(newData);
            axios.post(BASE_URL + "/api/pullLists", newData, { withCredentials: true }).then(() => {
                const updatedForm = {
                    isNewSeries: false,
                    pullListFile: null,
                    sampleQualityFile: null,
                    disposalRequestFile: null,
                    data: newData,
                    pullLists: editForm.pullLists,
                };

                updatedForm.pullLists.unshift(newData);
                updateForm(index, updatedForm);
                dispatchEditForm({ type: "UPDATE_FORM", updatedForm: updatedForm });

                if (shouldCloseSubmit) {
                    setShouldClose(true);
                } else {
                    setShouldClose(false);
                }
            });
        } else {
            if (shouldCloseSubmit) {
                setShouldClose(true);
            } else {
                setShouldClose(false);
            }
        }
    };

    const handleApproved = (e) => {
        e.preventDefault();

        if (!isEPApproved) {
            showErrorToast("The EP must be approved before approving the pull list.", <DangerousIcon style={{ color: "#ef4337" }} />);
            return false;
        }

        let approvedData = {
            ...editForm.data,
            updatedAt: new Date().toISOString(),
            updatedBy: user.name,
            createdAt: null,
            version: 1 + form.data.version,
            status: "Pull List Approved",
            approvalBy: user.name,
            approvalDate: editForm.data.approvalDate ? editForm.data.approvalDate : new Date().toISOString().slice(0, 10),
        };

        setIsApproving(true);
        loadData(approvedData, false).then(() => {
            setIsApproving(false);
            showToast("The workflow has been approved.", <DoneIcon style={{ color: "#61c313" }} />);
        });
    };

    const handleShipped = (e) => {
        e.preventDefault();

        if (!isEPApproved) {
            showErrorToast("The EP must be approved before submitting.", <DangerousIcon style={{ color: "#ef4337" }} />);
            return false;
        }

        let shippedData = lab ?
            {
                ...editForm.data,
                updatedAt: new Date().toISOString(),
                updatedBy: user.name,
                createdAt: null,
                version: 1 + form.data.version,
                status: "Submitted to Repository",
                submitDate: editForm.data.submitDate ? editForm.data.submitDate : new Date().toISOString().slice(0, 10),
                labName: lab.LabName,
                labDesc: lab.LabDesc,
                labAddress1: lab.ShippingAddress1,
                labAddress2: lab.ShippingAddress2,
                labAddress3: lab.ShippingAddress3,
                labAddress4: lab.ShippingAddress4,
                labCity: lab.ShippingCity,
                labState: lab.ShippingState,
                labPostalCode: lab.ShippingPostalCode,
                labContact: `${lab.ContactFirstName} ${lab.ContactLastName}`.trim(),
                labPhoneNumber: lab.PhoneNumber,
                labEmail: lab.EmailAddress,
                labPI: lab.PI,
            } :
            {
                ...editForm.data,
                updatedAt: new Date().toISOString(),
                updatedBy: user.name,
                createdAt: null,
                version: 1 + form.data.version,
                status: "Submitted to Repository",
                submitDate: editForm.data.submitDate ? editForm.data.submitDate : new Date().toISOString().slice(0, 10),
                labLegacyAddress: labLegacyAddress,
            };

        setIsShipping(true);
        loadData(shippedData, false).then(() => {
            setIsShipping(false);
            showToast("The workflow has been submitted.", <LocalShippingIcon style={{ color: "#61c313" }} />);
        });
    };

    const handleSubmit = async (e, shouldCloseSubmit) => {
        e.preventDefault();

        // Check required fields
        let hasEmptyRequiredFields = false;
        const emptyRequiredFields = [];

        required.forEach((fieldName) => {
            if (!editForm.data[fieldName]) {
                hasEmptyRequiredFields = true;
                emptyRequiredFields.push(fieldName);

                const element = document.querySelector(`#${fieldName} :nth-child(2)`);
                const uploadElement = document.querySelector(`#${fieldName} > div[style*="background-color: rgb(242, 243, 246);"]`);
                if (element || uploadElement) {
                    element?.classList.add("requiredHighlight");
                    element.querySelector("div[role='button']")?.classList.add("requiredHighlight");
                    uploadElement?.classList.add("requiredHighlight");
                }
            }
        });

        if (hasEmptyRequiredFields) {
            if (emptyRequiredFields.length > 0) {
                const element = document.getElementById(emptyRequiredFields[0]);
                if (element) {
                    element.scrollIntoView({ behavior: "smooth" });
                }
            }

            //console.log(emptyRequiredFields);
            showErrorToast("The highlighted required fields have not been filled out.", <DangerousIcon style={{ color: "#ef4337" }} />);
            return false;
        }

        const newData = { ...editForm.data };
        newData.updatedAt = new Date().toISOString();
        newData.updatedBy = user.name;
        if (sampleDisposal === "Yes") {
            newData.shipmentFromRepo = "Sample Disposal";
        }

        if (editForm.isNewSeries) {
            const response = await axios.post(BASE_URL + "/api/box/folder/new", {
                parentFolderId: pullListFolderId,
                folderName: "PL" + String(form.data.pullListId).padStart(2, "0") + " Files",
            }, { withCredentials: true });
            newData.boxId = pullListFolderId;
            newData.boxFilesId = response.data.id;
        } else {
            newData.createdAt = null;
            newData.version = 1 + form.data.version;
        }

        await loadData(newData, shouldCloseSubmit);
    };

    const handleSave = (e) => {
        setIsSaving(true);
        handleSubmit(e, false).then((r) => {
            if (r !== false) {
                setIsSaving(false);
                showToast("The workflow has been saved.", <SaveIcon style={{ color: "#2155bc" }} />);
            } else {
                setIsSaving(false);
            }
        });
    };

    const handleSaveAndClose = (e) => {
        setIsSavingClosing(true);
        handleSubmit(e, true).then((r) => {
            if (r !== false) {
                setIsSavingClosing(false);
                showToast("Pull list has been saved.", <SaveIcon style={{ color: "#2155bc" }} />);
            } else {
                setIsSavingClosing(false);
            }
        });
    };

    return (
        <Dialog
            open={isOpen}
            maxWidth={false}
            fullWidth
            onClose={(event, reason) => {
                if (reason === "backdropClick" || reason === "escapeKeyDown") { }
            }}
        >
            <DialogTitle
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                }}
            >
                <span>
                    EP{editForm.data.epId} - Series {editForm.data.pullListId}
                </span>
                <div className="pullListFormEdit">
                    <UnloadPrompt
                        currentData={editForm.data}
                        originalData={form.data}
                        files={[editForm.pullListFile]}
                        setHasUnsavedChanges={setHasUnsavedChanges}
                    />
                    {checkRole(user, [ROLES.APPROVER]) &&
                        form.data.status === "Pending Pull List Approval" &&
                        form.data.pullListFile &&
                        ["informedConsent", "informedConsentApprover"].every(key => ["Yes", "Non-ITN"].includes(editForm.data[key]) && ["Yes", "Non-ITN"].includes(form.data[key])) &&
                        ((editForm.data.shipmentFromRepo === "Yes" && editForm.data.shippingTemperature && editForm.data.dispositionOfSample) || sampleDisposal === "Yes") && (
                            <button
                                onClick={!isApproving ? handleApproved : undefined}
                                className="pullListFormEditSaveButton"
                            >
                                {isApproving ? (
                                    <CircularProgress
                                        size={18}
                                        style={{ color: "white", marginRight: "2px" }}
                                    />
                                ) : (
                                    <DoneIcon fontSize="small" />
                                )}
                                APPROVE
                            </button>
                        )}
                    {checkRole(user, [ROLES.BIOOPS]) &&
                        form.data.status === "Pull List Approved" &&
                        form.data.pullListFile &&
                        ["informedConsent", "informedConsentApprover"].every(key => ["Yes", "Non-ITN"].includes(editForm.data[key]) && ["Yes", "Non-ITN"].includes(form.data[key])) &&
                        (((editForm.data.shipmentFromRepo === "Yes" && editForm.data.courier && editForm.data.trackingNumber) || sampleDisposal === "Yes") && editForm.data.serviceRequestNumber) && (
                            <button
                                onClick={!isShipping ? handleShipped : undefined}
                                className="pullListFormEditSaveButton"
                            >
                                {isShipping ? (
                                    <CircularProgress
                                        size={18}
                                        style={{ color: "white", marginRight: "2px" }}
                                    />
                                ) : (
                                    <LocalShippingIcon fontSize="small" />
                                )}
                                SUBMIT TO REPO
                            </button>
                        )}
                    <button
                        onClick={!isSaving ? handleSave : undefined}
                        className="pullListFormEditSaveButton"
                    >
                        {isSaving ? (
                            <CircularProgress
                                size={18}
                                style={{ color: "white", marginRight: "2px" }}
                            />
                        ) : (
                            <SaveIcon fontSize="small" />
                        )}
                        SAVE
                    </button>
                    <button
                        onClick={!isSavingClosing ? handleSaveAndClose : undefined}
                        className="pullListFormEditSaveButton"
                    >
                        {isSavingClosing ? (
                            <CircularProgress
                                size={18}
                                style={{ color: "white", marginRight: "2px" }}
                            />
                        ) : (
                            <SaveIcon fontSize="small" />
                        )}
                        SAVE & CLOSE
                    </button>
                    <button onClick={onClose} className="pullListFormEditCloseButton">
                        <CloseIcon fontSize="small" />
                        CLOSE
                    </button>
                </div>
            </DialogTitle>
            <DialogContent>
                <div className="planFields">
                    {sampleDisposal === "No" &&
                        <ShipmentFromRepo
                            shipmentFromRepo={editForm.data.shipmentFromRepo}
                            handleWorkflowChange={handleWorkflowChange}
                            pullLists={editForm.pullLists}
                        />
                    }
                    {sampleDisposal === "Yes" &&
                        <DownloadUploadFields
                            category={"disposalRequestFile"}
                            handleFileChange={handleFileChange}
                            form={editForm}
                            pullLists={editForm.pullLists}
                            allowedRoles={ROLES.ELEVATED}
                            tag={ROLES.BIOOPS}
                            required={true}
                        />
                    }
                    <DownloadUploadFields
                        category={"pullListFile"}
                        handleFileChange={handleFileChange}
                        form={editForm}
                        pullLists={editForm.pullLists}
                        allowedRoles={ROLES.ELEVATED}
                        tag={ROLES.BIOOPS}
                        required={editForm.data.shipmentFromRepo === "Yes" || sampleDisposal === "Yes"}
                        disabled={!(form.data.status === "Real Time" || form.data.status === "Pull List Development" || form.data.status === "Pending Pull List Approval")}
                    />
                    {editForm.data.pullListFile &&
                        <FileLoadStatus
                            fileLoadStatus={editForm.data.fileLoadStatus}
                        />
                    }
                    {sampleDisposal === "No" &&
                        <DownloadUploadFields
                            category={"sampleQualityFile"}
                            handleFileChange={handleFileChange}
                            form={editForm}
                            pullLists={editForm.pullLists}
                            allowedRoles={ROLES.ELEVATED}
                            tag={ROLES.BIOOPS}
                        />
                    }
                    <Status
                        status={editForm.data.status}
                        handleChange={handleChange}
                        pullLists={editForm.pullLists}
                        informedConsent={editForm.data.informedConsent}
                        shipmentFromRepo={editForm.data.shipmentFromRepo}
                        shipDate={editForm.data.shipDate}
                        validationStatus={editForm.data.validationStatus}
                        sampleDisposal={sampleDisposal}
                    />
                    <RushOrder
                        rushOrder={editForm.data.rushOrder}
                        handleChange={handleChange}
                        pullLists={editForm.pullLists}
                    />
                    {sampleDisposal === "Yes" &&
                        <DisposalReason
                            disposalReason={editForm.data.disposalReason}
                            handleChange={handleChange}
                            pullLists={editForm.pullLists}
                        />
                    }
                    <PullListComment
                        pullListComment={editForm.data.pullListComment}
                        handleChange={handleChange}
                        pullLists={editForm.pullLists}
                        required={editForm.data.disposalReason === "Other (specify below)"}
                    />
                    <NumberOfSamples
                        numberOfSamples={editForm.data.numberOfSamples}
                        handleChange={handleChange}
                        pullLists={editForm.pullLists}
                    />
                    {(editForm.data.shipmentFromRepo === "Yes" && sampleDisposal === "No") &&
                        <ShippingTemperature
                            shippingTemperature={editForm.data.shippingTemperature}
                            handleChange={handleChange}
                            pullLists={editForm.pullLists}
                            required={editForm.data.shipmentFromRepo === "Yes"}
                        />
                    }
                    {sampleDisposal === "No" &&
                        <DispositionOfSample
                            dispositionOfSample={editForm.data.dispositionOfSample}
                            handleChange={handleChange}
                            pullLists={editForm.pullLists}
                            required={editForm.data.shipmentFromRepo === "Yes"}
                        />
                    }
                    {sampleDisposal === "No" && (
                        form.data.labName !== null ? (
                            <div className="planField">
                                <div className="notVisible">
                                    <GroupBiorepositoryOps/>
                                </div>
                                <LabDetails
                                    labName={form.data.labName}
                                    labDesc={form.data.labDesc}
                                    labAddress1={form.data.labAddress1}
                                    labAddress2={form.data.labAddress2}
                                    labAddress3={form.data.labAddress3}
                                    labAddress4={form.data.labAddress4}
                                    labCity={form.data.labCity}
                                    labState={form.data.labState}
                                    labPostalCode={form.data.labPostalCode}
                                    labContact={form.data.labContact}
                                    labPhoneNumber={form.data.labPhoneNumber}
                                    labEmail={form.data.labEmail}
                                    labPI={form.data.labPI}
                                />
                            </div>
                        ) : (
                            lab ? (
                                <div className="planField">
                                    <div className="notVisible">
                                        <GroupBiorepositoryOps/>
                                    </div>
                                    <LabDetails lab={lab}/>
                                </div>
                            ) : labLegacyAddress && (
                                <div className="planField">
                                    <div className="notVisible">
                                        <GroupBiorepositoryOps/>
                                    </div>
                                    <LegacyLabsDetails labLegacyAddress={labLegacyAddress}/>
                                </div>
                            )
                        )
                    )}
                    {(editForm.data.shipmentFromRepo === "Yes" || sampleDisposal === "Yes") &&
                        <>
                            <InformedConsent
                                informedConsent={editForm.data.informedConsent}
                                informedConsentApprover={editForm.data.informedConsentApprover}
                                handleConsentChange={handleConsentChange}
                                shipmentFromRepo={editForm.data.shipmentFromRepo}
                                sampleDisposal={sampleDisposal}
                                pullLists={editForm.pullLists}
                                required={required}
                            />
                            <InformedConsentApprover
                                informedConsentApprover={editForm.data.informedConsentApprover}
                                informedConsent={editForm.data.informedConsent}
                                handleConsentChange={handleConsentChange}
                                shipmentFromRepo={editForm.data.shipmentFromRepo}
                                sampleDisposal={sampleDisposal}
                                pullLists={editForm.pullLists}
                                required={required}
                            />
                            <InformedConsentComment
                                informedConsentComment={editForm.data.informedConsentComment}
                                handleChange={handleChange}
                                shipmentFromRepo={editForm.data.shipmentFromRepo}
                                pullLists={editForm.pullLists}
                                required={required}
                            />
                        </>
                    }
                    <DateSelector
                        category={"approvalDate"}
                        date={editForm.data.approvalDate}
                        handleChange={handleChange}
                        pullLists={editForm.pullLists}
                        roles={[ROLES.APPROVER, ROLES.BIOOPS]}
                        informedConsent={editForm.data.informedConsent}
                        informedConsentApprover={editForm.data.informedConsentApprover}
                        shipmentFromRepo={editForm.data.shipmentFromRepo}
                        sampleDisposal={sampleDisposal}
                    />
                    {(editForm.data.shipmentFromRepo === "Yes" && sampleDisposal === "No") &&
                        <>
                            <Courier
                                courier={editForm.data.courier}
                                handleChange={handleChange}
                                pullLists={editForm.pullLists}
                                couriers={couriers}
                                informedConsent={editForm.data.informedConsent}
                                informedConsentApprover={editForm.data.informedConsentApprover}
                                required={form.data.status === "Pull List Approved" || form.data.status === "Pull List Executed"}
                            />
                            <TrackingNumber
                                trackingNumber={editForm.data.trackingNumber}
                                handleChange={handleChange}
                                pullLists={editForm.pullLists}
                                informedConsent={editForm.data.informedConsent}
                                informedConsentApprover={editForm.data.informedConsentApprover}
                                sampleDisposal={sampleDisposal}
                                required={(editForm.data.shipmentFromRepo === "Yes" && sampleDisposal === "No") && (form.data.status === "Pull List Approved" || form.data.status === "Pull List Executed")}
                            />
                        </>
                    }
                    {(editForm.data.shipmentFromRepo === "Yes" || sampleDisposal === "Yes") &&
                        <>
                            <ServiceRequestNumber
                                serviceRequestNumber={editForm.data.serviceRequestNumber}
                                handleChange={handleChange}
                                pullLists={editForm.pullLists}
                                informedConsent={editForm.data.informedConsent}
                                informedConsentApprover={editForm.data.informedConsentApprover}
                                sampleDisposal={sampleDisposal}
                                required={(editForm.data.shipmentFromRepo === "Yes" || sampleDisposal === "Yes") && (form.data.status === "Pull List Approved" || form.data.status === "Pull List Executed")}
                            />
                            <DateSelector
                                category={"submitDate"}
                                date={editForm.data.submitDate}
                                approvalDate={editForm.data.approvalDate}
                                handleChange={handleChange}
                                pullLists={editForm.pullLists}
                                allowedRoles={[ROLES.BIOOPS, ROLES.APPROVER]}
                                informedConsent={editForm.data.informedConsent}
                                informedConsentApprover={editForm.data.informedConsentApprover}
                                shipmentFromRepo={editForm.data.shipmentFromRepo}
                                sampleDisposal={sampleDisposal}
                                required={form.data.status === "Pull List Approved"}
                            />
                            <DateSelector
                                category={"shipDate"}
                                label={sampleDisposal === "Yes" ? "disposalDate" : "shipDate"}
                                date={editForm.data.shipDate}
                                approvalDate={editForm.data.approvalDate}
                                handleChange={handleChange}
                                pullLists={editForm.pullLists}
                                allowedRoles={[ROLES.BIOOPS, ROLES.APPROVER]}
                                informedConsent={editForm.data.informedConsent}
                                informedConsentApprover={editForm.data.informedConsentApprover}
                                shipmentFromRepo={editForm.data.shipmentFromRepo}
                                sampleDisposal={sampleDisposal}
                                required={form.data.status === "Submitted to Repository" || form.data.status === "Pull List Executed"}
                            />
                        </>
                    }
                    <DateSelector
                        category={"expectedDataDate"}
                        label={sampleDisposal === "Yes" ? "expectedCompletionDate" : "expectedDataDate"}
                        date={editForm.data.expectedDataDate}
                        approvalDate={editForm.data.approvalDate}
                        handleChange={handleChange}
                        pullLists={editForm.pullLists}
                        allowedRoles={ROLES.ALL}
                        informedConsent={editForm.data.informedConsent}
                        informedConsentApprover={editForm.data.informedConsentApprover}
                        shipmentFromRepo={editForm.data.shipmentFromRepo}
                        sampleDisposal={sampleDisposal}
                    />
                    <EPUpdate
                        epUpdate={editForm.data.epUpdate}
                        handleChange={handleChange}
                        pullLists={editForm.pullLists}
                        informedConsent={editForm.data.informedConsent}
                        informedConsentApprover={editForm.data.informedConsentApprover}
                        shipmentFromRepo={editForm.data.shipmentFromRepo}
                        sampleDisposal={sampleDisposal}
                    />
                    <DateSelector
                        category={"resultsReceivedDate"}
                        label={sampleDisposal === "Yes" ? "completionDate" : "resultsReceivedDate"}
                        date={editForm.data.resultsReceivedDate}
                        handleChange={handleChange}
                        pullLists={editForm.pullLists}
                        allowedRoles={[ROLES.DCT]}
                        informedConsent={editForm.data.informedConsent}
                        informedConsentApprover={editForm.data.informedConsentApprover}
                        shipmentFromRepo={editForm.data.shipmentFromRepo}
                        sampleDisposal={sampleDisposal}
                    />
                    <DateSelector
                        category={"resultsValidatedDate"}
                        date={editForm.data.resultsValidatedDate}
                        handleChange={handleChange}
                        pullLists={editForm.pullLists}
                        allowedRoles={[ROLES.DCT]}
                        informedConsent={editForm.data.informedConsent}
                        informedConsentApprover={editForm.data.informedConsentApprover}
                        shipmentFromRepo={editForm.data.shipmentFromRepo}
                        sampleDisposal={sampleDisposal}
                    />
                    <ValidationStatus
                        validationStatus={editForm.data.validationStatus}
                        handleChange={handleChange}
                        pullLists={editForm.pullLists}
                        informedConsent={editForm.data.informedConsent}
                        informedConsentApprover={editForm.data.informedConsentApprover}
                        shipmentFromRepo={editForm.data.shipmentFromRepo}
                        sampleDisposal={sampleDisposal}
                    />
                    <ValidationBy
                        validationBy={editForm.data.validationBy}
                        handleChange={handleChange}
                        pullLists={editForm.pullLists}
                        informedConsent={editForm.data.informedConsent}
                        informedConsentApprover={editForm.data.informedConsentApprover}
                        shipmentFromRepo={editForm.data.shipmentFromRepo}
                        sampleDisposal={sampleDisposal}
                    />
                    <ValidationNotesText
                        validationNotesText={editForm.data.validationNotesText}
                        handleChange={handleChange}
                        pullLists={editForm.pullLists}
                        informedConsent={editForm.data.informedConsent}
                        informedConsentApprover={editForm.data.informedConsentApprover}
                        shipmentFromRepo={editForm.data.shipmentFromRepo}
                        sampleDisposal={sampleDisposal}
                    />
                    <DatasetsValidationLink
                        datasetsValidationLink={editForm.data.datasetsValidationLink}
                        handleChange={handleChange}
                        pullLists={editForm.pullLists}
                        informedConsent={editForm.data.informedConsent}
                        informedConsentApprover={editForm.data.informedConsentApprover}
                        shipmentFromRepo={editForm.data.shipmentFromRepo}
                        sampleDisposal={sampleDisposal}
                    />
                    <RelatedJiraTickets
                        relatedJiraTickets={editForm.data.relatedJiraTickets}
                        handleChange={handleChange}
                        pullLists={editForm.pullLists}
                        informedConsent={editForm.data.informedConsent}
                        informedConsentApprover={editForm.data.informedConsentApprover}
                        shipmentFromRepo={editForm.data.shipmentFromRepo}
                        sampleDisposal={sampleDisposal}
                    />
                    {!editForm.isNewSeries && <div className="xplanLine"/>}
                    {!editForm.isNewSeries &&
                        <BoxFileManager
                            folderId={editForm.data.boxFilesId}
                            title={`EP${form.data.epId} Series ${form.data.pullListId} Files`}
                            allowedRoles={ROLES.ELEVATED}
                        />
                    }
                </div>
                <div className="xplanLine"/>
                <HistoryLogTotal pullLists={editForm.pullLists}/>
            </DialogContent>
        </Dialog>
    );
};

export default PullListFormEdit;
